/**
 * Custom hook for Application User API operations
 * 
 * @example
 * // Basic usage - Create application user
 * const { createUser, isCreating } = useApplicationUser();
 * 
 * // Submit a new user
 * const handleCreateUser = (userData: UserDto) => {
 *   createUser(userData);
 * };
 * 
 * @example
 * // Using the mutation directly
 * const createMutation = useCreateApplicationUser();
 * 
 * // Submit user
 * createMutation.mutate(userData);
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useError } from "../contexts/ErrorContext";
import { useTranslation } from "react-i18next";

const API_BASE_URL = process.env.REACT_APP_API_URL ;

// TypeScript interface matching UserDto
export interface UserDto {
  id?: number;
  username?: string;
  email?: string;
  password?: string;
  roles?: string[]; // Frontend uses array, backend Set<String> will be converted
  schoolId?: number;
  status?: boolean; // Changed from 'enabled' to 'status' to match backend
  createdAt?: string;
}

// API Functions

/**
 * Get all application users by school ID
 * @returns Promise with list of users
 */
const getApplicationUsersBySchoolId = async (): Promise<UserDto[]> => {
  const response = await axios.get(
    `${API_BASE_URL}/application_user`
  );

  // Clean user data to remove duplicate 'role' field
  const cleanedUsers = response.data.map((user: any) => {
    const { role, ...cleanUser } = user; // Remove duplicate 'role' field
    return cleanUser;
  });

  return cleanedUsers;
};

/**
 * Create a new application user
 * @param userData - User data to create
 * @returns Promise with created user data
 */
const createApplicationUser = async (userData: UserDto): Promise<UserDto> => {
  const response = await axios.post(
    `${API_BASE_URL}/application_user`,
    userData,
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  // Clean response data to remove duplicate 'role' field
  const { role, ...cleanUser } = response.data;
  return cleanUser;
};

/**
 * Update an existing application user
 * @param userData - User data to update
 * @returns Promise with updated user data
 */
const updateApplicationUser = async (userData: UserDto): Promise<UserDto> => {
  const response = await axios.put(
    `${API_BASE_URL}/application_user`,
    userData,
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  // Clean response data to remove duplicate 'role' field
  const { role, ...cleanUser } = response.data;
  return cleanUser;
};

/**
 * Delete an application user by email
 * @param email - Email of the user to delete
 * @returns Promise with void response
 */
const deleteApplicationUser = async (email: string): Promise<void> => {
  await axios.delete(
    `${API_BASE_URL}/application_user/${email}`
  );
};

// Custom Hooks

/**
 * Hook to fetch application users by school ID
 * @returns Query object for fetching users
 */
export const useGetApplicationUsers = () => {
  const { showError } = useError();

  return useQuery({
    queryKey: ["applicationUsers"],
    queryFn: getApplicationUsersBySchoolId,
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch application users. Please try again.";
      showError(errorMessage);
    },
  });
};

/**
 * Hook to create a new application user
 * @returns Mutation object for creating users
 */
export const useCreateApplicationUser = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useError();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userData: UserDto) => createApplicationUser(userData),
    onSuccess: (data) => {
      // Invalidate and refetch users queries if needed
      queryClient.invalidateQueries({ 
        queryKey: ["users"] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ["applicationUsers"] 
      });
      showSuccess(t("application_user_created_successfully") || "Application user created successfully!");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "";
      
      const errorLower = errorMessage.toLowerCase();
      
      // Check for duplicate email error - handle various formats
      if (errorLower.includes('duplicate key value violates unique constraint') && 
          (errorLower.includes('uk_users_email') || errorLower.includes('email'))) {
        showError(t("email_already_exists") || "Email already exists");
      } else if (errorLower.includes('email') && 
          (errorLower.includes('already') || 
           errorLower.includes('exist') ||
           errorLower.includes('duplicate') ||
           errorLower.includes('taken'))) {
        showError(t("email_already_exists") || "Email already exists");
      } else if (errorMessage) {
        showError(errorMessage);
      } else {
        showError(t("error_creating_application_user") || "Failed to create application user. Please try again.");
      }
    },
  });
};

/**
 * Hook to update an existing application user
 * @returns Mutation object for updating users
 */
export const useUpdateApplicationUser = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useError();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userData: UserDto) => updateApplicationUser(userData),
    onSuccess: (data) => {
      // Invalidate and refetch users queries if needed
      queryClient.invalidateQueries({ 
        queryKey: ["users"] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ["applicationUsers"] 
      });
      showSuccess(t("application_user_updated_successfully") || "Application user updated successfully!");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "";
      
      const errorLower = errorMessage.toLowerCase();
      
      // Check for duplicate email error - handle various formats
      if (errorLower.includes('duplicate key value violates unique constraint') && 
          (errorLower.includes('uk_users_email') || errorLower.includes('email'))) {
        showError(t("email_already_exists") || "Email already exists");
      } else if (errorLower.includes('email') && 
          (errorLower.includes('already') || 
           errorLower.includes('exist') ||
           errorLower.includes('duplicate') ||
           errorLower.includes('taken'))) {
        showError(t("email_already_exists") || "Email already exists");
      } else if (errorMessage) {
        showError(errorMessage);
      } else {
        showError(t("error_updating_application_user") || "Failed to update application user. Please try again.");
      }
    },
  });
};

/**
 * Hook to delete an application user by email
 * @returns Mutation object for deleting users
 */
export const useDeleteApplicationUser = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useError();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (email: string) => deleteApplicationUser(email),
    onSuccess: () => {
      // Invalidate and refetch users queries if needed
      queryClient.invalidateQueries({ 
        queryKey: ["users"] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ["applicationUsers"] 
      });
      showSuccess(t("application_user_deleted_successfully") || "Application user deleted successfully!");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        t("error_deleting_application_user") || "Failed to delete application user. Please try again.";
      showError(errorMessage);
    },
  });
};

/**
 * Combined hook that provides all application user operations
 * @returns Object containing all user-related operations
 */
export const useApplicationUser = () => {
  const createMutation = useCreateApplicationUser();
  const updateMutation = useUpdateApplicationUser();
  const deleteMutation = useDeleteApplicationUser();
  const usersQuery = useGetApplicationUsers();

  return {
    // Query
    applicationUsers: usersQuery.data || [],
    isLoading: usersQuery.isLoading,
    isFetching: usersQuery.isFetching,
    isError: usersQuery.isError,
    error: usersQuery.error,
    refetch: usersQuery.refetch,
    // Create Mutation
    createUser: createMutation.mutate,
    createUserAsync: createMutation.mutateAsync,
    isCreating: createMutation.isLoading,
    createError: createMutation.error,
    createSuccess: createMutation.isSuccess,
    // Update Mutation
    updateUser: updateMutation.mutate,
    updateUserAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isLoading,
    updateError: updateMutation.error,
    updateSuccess: updateMutation.isSuccess,
    // Delete Mutation
    deleteUser: deleteMutation.mutate,
    deleteUserAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isLoading,
    deleteError: deleteMutation.error,
    deleteSuccess: deleteMutation.isSuccess,
  };
};

